import { BellOff, LogIn, LogOut, Pause, WifiOff, type LucideIcon } from 'lucide-react';
import type { QueueAgent } from '@/types';

/**
 * How an agent stands in one queue, most severe first when several apply:
 * logged out, paused, offline (no registered phone/Linkus), on Do Not Disturb,
 * otherwise signed in and able to take queue calls.
 */
export type MembershipState = 'signed_in' | 'offline' | 'dnd' | 'paused' | 'logged_out';

export const STATE_ORDER: MembershipState[] = [
  'signed_in',
  'offline',
  'dnd',
  'paused',
  'logged_out',
];

/** States that mean the agent will not get queue calls right now. */
export const PROBLEM_STATES: Exclude<MembershipState, 'signed_in'>[] = [
  'offline',
  'dnd',
  'paused',
  'logged_out',
];

export const STATE_META: Record<
  MembershipState,
  {
    label: string;
    description: string;
    icon: LucideIcon;
    dot: string;
    chip: string;
    text: string;
    tile: string;
  }
> = {
  signed_in: {
    label: 'Signed in',
    description: 'Signed in and reachable',
    icon: LogIn,
    dot: 'bg-primary-500',
    chip: 'bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-200',
    text: 'text-primary-700 dark:text-primary-400',
    tile: 'bg-primary-50 dark:bg-primary-900/20',
  },
  paused: {
    label: 'Paused',
    description: 'Paused in this queue',
    icon: Pause,
    dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    text: 'text-amber-700 dark:text-amber-400',
    tile: 'bg-amber-50 dark:bg-amber-900/20',
  },
  offline: {
    label: 'Offline',
    description: 'No phone or Linkus app registered',
    icon: WifiOff,
    dot: 'bg-slate-600 dark:bg-slate-400',
    chip: 'bg-slate-700 text-white dark:bg-slate-600 dark:text-white',
    text: 'text-slate-700 dark:text-slate-300',
    tile: 'bg-slate-100 dark:bg-slate-700/50',
  },
  dnd: {
    label: 'Do Not Disturb',
    description: 'Extension presence is set to DND',
    icon: BellOff,
    dot: 'bg-violet-500',
    chip: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
    text: 'text-violet-700 dark:text-violet-400',
    tile: 'bg-violet-50 dark:bg-violet-900/20',
  },
  logged_out: {
    label: 'Logged out',
    description: 'Logged out of this queue',
    icon: LogOut,
    dot: 'bg-rose-500',
    chip: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200',
    text: 'text-rose-700 dark:text-rose-400',
    tile: 'bg-rose-50 dark:bg-rose-900/20',
  },
};

export const CALL_STATE: Record<
  QueueAgent['status'],
  { label: string; className: string }
> = {
  idle: { label: 'Available', className: 'text-primary-600 dark:text-primary-400' },
  busy: { label: 'On call', className: 'text-red-600 dark:text-red-400' },
  ringing: { label: 'Ringing', className: 'text-amber-600 dark:text-amber-400' },
  unavailable: { label: 'Unavailable', className: 'text-gray-500 dark:text-gray-400' },
};

/** No registered device; falls back to the queue's own "unknown" call status. */
export function isAgentOffline(agent: QueueAgent) {
  if (agent.registered !== undefined) return !agent.registered;
  return agent.status === 'unavailable';
}

export function membershipState(agent: QueueAgent): MembershipState {
  if (!agent.logged_in) return 'logged_out';
  if (agent.paused) return 'paused';
  if (isAgentOffline(agent)) return 'offline';
  if (agent.presence === 'do_not_disturb') return 'dnd';
  return 'signed_in';
}

export function agentKey(agent: QueueAgent) {
  return agent.agent_num || agent.agent_id;
}

export function agentLabel(agent: QueueAgent) {
  return agent.agent_name || `Ext ${agent.agent_num}`;
}

export const byText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export function countStates(items: Array<{ state: MembershipState }>) {
  const counts = Object.fromEntries(STATE_ORDER.map((state) => [state, 0])) as Record<
    MembershipState,
    number
  >;
  items.forEach((item) => counts[item.state]++);
  return counts;
}
